/**
 * Typed client for the two Functions backends: the Private API (personal data
 * and mutations) and the Public API (anonymous-friendly reads + community
 * contributions). Azure Static Web Apps / the local Next.js dev proxy route
 * "/api/*" to the Public Functions app and everything else in "/api/*"
 * to the Private one (see next.config.ts).
 */
import { Signal, getBearerToken, showToast } from '../state/appState'
import {
  cachePutResponse,
  getCachedResponse,
  patchCachedRepertoireItem,
  queuePendingMutation,
  type PendingMutation,
} from './offlineDb'
import { triggerSync } from './syncManager'

const API_BASE = 'https://api-private.sessionr.org/api/'
const PUBLIC_API_BASE = 'https://api-public.sessionr.org/api/'

// Hard ceiling on any single request. Without it a stalled connection (e.g. a
// slow /users/me) leaves callers — including the "Completing sign-in" modal —
// hanging indefinitely. A timed-out request is routed through the same
// unreachable/offline path as a dropped connection.
const REQUEST_TIMEOUT_MS = 20000

export const isApiOffline = new Signal(false)

let usersMeRetryTimer: ReturnType<typeof setInterval> | null = null

/**
 * Probes whether the private Functions backend is reachable, and latches
 * `isApiOffline` when it isn't.
 *
 * Strictly a *connectivity* check. It must never conclude "offline" from an
 * authentication problem: `isApiOffline` gates every request in doFetch —
 * public and anonymous reads included — and its retry timer re-runs this every
 * two minutes, so a wrong latch takes the whole app offline until reload.
 */
export async function checkUsersMeHealth(): Promise<boolean> {
  try {
    const token = await getBearerToken(false)
    if (!token) {
      // No token says nothing about the backend. Probing unauthenticated would
      // earn a guaranteed 401 and latch isApiOffline against a backend that is
      // perfectly healthy — which is how an expired session used to present as
      // "the Functions host is down".
      return false
    }
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
    const cleanBase = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE
    const res = await fetch(`${cleanBase}/users/me`, {
      method: 'GET',
      headers,
    })

    if (res.ok) {
      if (isApiOffline.value) {
        isApiOffline.value = false
        if (usersMeRetryTimer) {
          clearInterval(usersMeRetryTimer)
          usersMeRetryTimer = null
        }
        void triggerSync()
      } else if (usersMeRetryTimer) {
        clearInterval(usersMeRetryTimer)
        usersMeRetryTimer = null
      }
      return true
    }

    if (!isBackendUnavailableStatus(res.status)) {
      // The backend answered, so it is up. A 401/403 means our token was
      // rejected and a 4xx means we asked wrongly — neither is "offline", and
      // latching on them strands the app. Auth recovery is AuthProvider's job.
      return false
    }
    markApiOfflineAndScheduleRetry()
    return false
  } catch {
    // A thrown fetch never reached a server (DNS/CORS/dropped connection).
    // That, and a 5xx above, are the only genuine "offline" evidence.
    markApiOfflineAndScheduleRetry()
    return false
  }
}

/**
 * Statuses that mean the backend itself is unavailable rather than that it
 * deliberately rejected the request — bad gateway, cold start, gateway timeout.
 */
export function isBackendUnavailableStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504
}

function markApiOfflineAndScheduleRetry(): void {
  isApiOffline.value = true
  if (!usersMeRetryTimer) {
    usersMeRetryTimer = setInterval(() => {
      void checkUsersMeHealth()
    }, 2 * 60 * 1000)
  }
}

async function doFetch<T>(base: string, path: string, init?: RequestInit, isRetry = false, opts?: { skipOfflineQueue?: boolean; withAuth?: boolean; timeoutMs?: number }): Promise<T> {
  const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  const url = `${cleanBase}${cleanPath}`
  const method = (init?.method ?? 'GET').toUpperCase()

  const isPrivateApi = cleanBase === (API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE)
  const isUsersMeCheck = isPrivateApi && (cleanPath === '/users/me' || cleanPath === 'users/me') && method === 'GET'

  // Every private call needs a token; a handful of Public routes do too. The moderator
  // endpoints are the case that forced this apart: refresh-match-index lives in the Public
  // app (that's the process owning the in-memory index) but still checks Entra group claims
  // off the bearer token, so "which API" no longer decides "does this send credentials".
  const useAuth = isPrivateApi || opts?.withAuth === true

  // Content-Type: application/json isn't a CORS-safelisted header, so sending it
  // unconditionally forces a preflight (OPTIONS) round trip on every single cross-origin
  // call, even bodiless GETs. Only set it when there's actually a JSON body to describe —
  // anonymous/public GETs with no Authorization header then qualify as CORS-simple requests
  // and skip preflight entirely.
  const headers: Record<string, string> = {
    'X-Timezone-Offset': String(new Date().getTimezoneOffset()),
    ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
    ...init?.headers as Record<string, string>,
  }

  let token: string | null = null
  if (useAuth) {
    try {
      token = await getBearerToken(isRetry)
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    } catch (err) {
      console.error('Failed to get bearer token', err)
    }
  }

  const knownOffline = typeof navigator !== 'undefined' && !navigator.onLine

  // Authenticated endpoints require a verified token. If no token is available,
  // do not fire the request unauthenticated (which will return 401/403 and, on the
  // private backend, trigger false offline checks).
  if (useAuth && !token) {
    // Offline, the token usually can't be refreshed at all — its silent renewal
    // needs the network. A GET can still be served from the offline cache, so
    // route it through the same unreachable path as a token-carrying request
    // whose fetch fails. This is what lets repertoire/tunes/users-me render
    // offline even once the cached access token has expired.
    if (method === 'GET' && knownOffline) {
      return handleUnreachable<T>(method, url, path, init)
    }
    if (isUsersMeCheck) {
      return null as unknown as T
    }
    throw new UnauthenticatedError('Sign in required for this action.')
  }

  // Skip straight to the offline path rather than waiting on a fetch that's
  // certain to fail — navigator.onLine is false only when the browser is
  // confident there's no connectivity at all. Also check isApiOffline.
  const isOffline = knownOffline || (isApiOffline.value && !isUsersMeCheck)

  let res: Response
  if (!isOffline) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), opts?.timeoutMs ?? REQUEST_TIMEOUT_MS)
    try {
      res = await fetch(url, { headers, ...init, signal: controller.signal })
    } catch (err) {
      // A thrown fetch means the request never reached a server (DNS/CORS/
      // dropped connection) — as opposed to an HTTP error status below,
      // which the backend actually responded to and shouldn't be treated
      // as "offline". An AbortError is our own timeout firing: treat a stalled
      // request the same as unreachable so callers can't hang forever.
      const isTimeout = err instanceof DOMException && err.name === 'AbortError'
      if (!isTimeout && !(err instanceof TypeError)) throw err
      if (isPrivateApi) {
        void checkUsersMeHealth()
      }
      return handleUnreachable<T>(method, url, path, init, opts)
    } finally {
      clearTimeout(timer)
    }
  } else {
    return handleUnreachable<T>(method, url, path, init, opts)
  }

  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    if (res.status === 401 && !isRetry) {
      console.warn('401 Unauthorized or expired token received, attempting token refresh and retry...')
      return doFetch<T>(base, path, init, true, opts)
    }

    // Only a genuinely unavailable backend (bad gateway / cold-start / gateway
    // timeout) should be treated like being offline and have the mutation
    // queued for later replay. A 4xx/5xx *with* a response body is a real
    // application error the server deliberately returned — surface it as an
    // ApiError (carrying the backend's { error, detail } diagnostic) instead of
    // silently swallowing it into the offline queue, where it would be invisible.
    const backendUnavailable = isBackendUnavailableStatus(res.status)
    if (isPrivateApi && method !== 'GET' && !opts?.skipOfflineQueue && backendUnavailable) {
      void checkUsersMeHealth()
      return queueOfflineMutation<T>(method, url, path, init)
    }

    throw new ApiError(res.status, res.statusText, detail)
  }

  let data: T
  if (res.status === 204) {
    data = undefined as T
  } else {
    const text = await res.text()
    data = (!text || !text.trim()) ? (undefined as T) : (JSON.parse(text) as T)
  }

  if (method === 'GET') {
    void cachePutResponse(url, data)
  }
  return data
}

async function handleUnreachable<T>(method: string, url: string, path: string, init?: RequestInit, opts?: { skipOfflineQueue?: boolean }): Promise<T> {
  if (method === 'GET') {
    const cached = await getCachedResponse<T>(url)
    if (cached !== undefined) return cached
    throw new Error('You appear to be offline, and no cached data is available for this yet.')
  }
  // Some mutations must never be replayed later: a moderator maintenance job may well still
  // be running on the server after our own timeout fired, and re-sending it is neither
  // harmless nor what the caller was told happened. Surface the failure instead of queueing.
  if (opts?.skipOfflineQueue) {
    throw new Error('The request could not be completed — the server may be unreachable, or the operation may still be running.')
  }
  return queueOfflineMutation<T>(method, url, path, init)
}

function classifyMutation(method: string, path: string): { mutationType: PendingMutation['mutationType']; description: string } {
  if (method === 'POST' && /\/repertoire\/items\/[^/]+\/practiced$/.test(path)) {
    return { mutationType: 'practice_log', description: 'Practice log' }
  }
  if ((method === 'PATCH' || method === 'DELETE') && /\/repertoire\/items\/[^/]+$/.test(path)) {
    return { mutationType: 'repertoire_patch', description: method === 'DELETE' ? 'Repertoire removal' : 'Repertoire update' }
  }
  if (path.startsWith('/sets')) {
    return { mutationType: 'set_mutation', description: 'Set change' }
  }
  return { mutationType: 'other', description: `${method} ${path}` }
}

export class UnauthenticatedError extends Error {
  unauthenticated = true
  constructor(message = 'Sign in required for this action.') {
    super(message)
    this.name = 'UnauthenticatedError'
  }
}

/**
 * Whether a thrown value means "you are not authenticated" rather than "the
 * backend is unreachable". Both surface as a failed react-query, but they need
 * very different messages: one is fixed by signing in, the other by waiting.
 *
 * Matches the `unauthenticated` flag structurally rather than with `instanceof`
 * so it survives errors that crossed a module or realm boundary, and also
 * covers a real 401 from the server.
 */
export function isUnauthenticatedError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  if ((err as { unauthenticated?: unknown }).unauthenticated === true) return true
  return err instanceof ApiError && err.status === 401
}

/** Marks a mutation as caught by the offline queue rather than genuinely failed. */
export class OfflineQueuedError extends Error {
  offlineQueued = true
  constructor(description: string) {
    super(`${description} — queued offline, will sync when back online.`)
    this.name = 'OfflineQueuedError'
  }
}

/**
 * A genuine error *response* from the server: the request reached the backend
 * and it replied with a non-2xx status. Carries the raw body plus, when the
 * backend returned our { error, detail } diagnostic shape, the parsed fields —
 * so the UI can show the real message and stack trace instead of a bare status.
 */
export class ApiError extends Error {
  status: number
  body: string
  serverError?: string
  serverDetail?: string
  constructor(status: number, statusText: string, body: string) {
    let serverError: string | undefined
    let serverDetail: string | undefined
    if (body) {
      try {
        const parsed = JSON.parse(body)
        if (parsed && typeof parsed === 'object') {
          if (typeof parsed.error === 'string') serverError = parsed.error
          if (typeof parsed.detail === 'string') serverDetail = parsed.detail
        }
      } catch {
        // Non-JSON body (e.g. a plain-text message) — preserved via `body`.
      }
    }
    super(serverError || body || `API ${status}: ${statusText}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
    this.serverError = serverError
    this.serverDetail = serverDetail
  }
}

async function queueOfflineMutation<T>(method: string, url: string, path: string, init?: RequestInit): Promise<T> {
  const { mutationType, description } = classifyMutation(method, path)
  const mutation: PendingMutation = {
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    url,
    method: method as PendingMutation['method'],
    body: typeof init?.body === 'string' ? init.body : undefined,
    description,
    mutationType,
  }
  await queuePendingMutation(mutation)

  // Practice logs and repertoire patches are the two flows with a defined
  // optimistic-success shape, so those return real data instead of erroring —
  // everything else (sets, groups, tune contributions, ...) is still safely
  // queued for replay, but doFetch can't fabricate a type-correct response
  // for every endpoint shape, so those surface as a catchable OfflineQueuedError.
  if (mutationType === 'practice_log') {
    const userTuneId = /\/repertoire\/items\/([^/]+)\/practiced$/.exec(path)?.[1]
    if (userTuneId) {
      const lastPracticed = new Date().toISOString()
      const itemUrl = url.replace(/\/practiced$/, '')
      await patchCachedRepertoireItem(itemUrl, userTuneId, { lastPracticed })
      showToast('Practice logged offline. Will sync when back online.', 'info')
      return { userTuneId, lastPracticed } as unknown as T
    }
  }

  if (mutationType === 'repertoire_patch' && method === 'PATCH') {
    const userTuneId = /\/repertoire\/items\/([^/]+)$/.exec(path)?.[1]
    if (userTuneId && typeof init?.body === 'string') {
      const patch = JSON.parse(init.body) as Record<string, unknown>
      // A setting switch changes the effective key and ABC, and only the server can resolve those
      // — merging the raw patch would leave the cache showing the old notes under the new setting.
      // The mutation is already queued above; just don't fabricate an optimistic response for it.
      const changesSetting = 'tuneSettingId' in patch || 'clearTuneSetting' in patch
      if (!changesSetting) {
        const merged = await patchCachedRepertoireItem(url, userTuneId, patch)
        if (merged) {
          showToast('Change saved offline. Will sync when back online.', 'info')
          return merged as unknown as T
        }
      }
    }
  }

  showToast(`${description} queued offline. Will sync when back online.`, 'info')
  throw new OfflineQueuedError(description)
}

/** Calls the Private API (personal data, mutations). */
export const apiFetch = <T>(path: string, init?: RequestInit, opts?: { skipOfflineQueue?: boolean; timeoutMs?: number }) =>
  doFetch<T>(API_BASE, path, init, false, opts)

/** Calls the Public API (anonymous-friendly reads + community contributions). */
export const apiFetchPublic = <T>(path: string, init?: RequestInit) => doFetch<T>(PUBLIC_API_BASE, path, init)

/**
 * Calls the Public API *with* the bearer token attached, for the few Public routes that
 * still authorize the caller (the moderator match-index refresh). Sending Authorization
 * cross-origin makes the request non-simple, so this needs the Public Function App's CORS
 * to allow the header and answer the OPTIONS preflight — unlike every other Public call,
 * which is deliberately kept CORS-simple.
 */
export const apiFetchPublicAuthed = <T>(path: string, init?: RequestInit) =>
  doFetch<T>(PUBLIC_API_BASE, path, init, false, { withAuth: true, skipOfflineQueue: true })

/**
 * Server timestamps are UTC but don't always carry the Z suffix (depends on
 * how the value round-tripped through the database), so normalize on parse.
 */
export function parseServerDate(value: string): Date {
  const hasZone = value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value)
  return new Date(hasZone ? value : `${value}Z`)
}

/** Display form of a TuneType enum name: "SlipJig" -> "Slip Jig". */
export function formatTuneType(type: string): string {
  return type.replace(/([a-z])([A-Z])/g, '$1 $2')
}

export function isToday(value: string): boolean {
  return parseServerDate(value).toDateString() === new Date().toDateString()
}

// --- Types (mirror the backend DTOs) ---
// IDs are UUID strings, except thesession.org's own numeric IDs (TheSessionResult,
// importFromTheSession, tunebook member IDs), which are that external system's IDs.

export interface TuneSummary {
  tuneId: string
  title: string
  tuneType: string
  key: string
  repertoireCount: number
  /** Rough 0–100 difficulty estimate (higher = harder). See api TuneHeuristics. */
  difficulty?: number
}

export interface TuneSetting {
  tuneSettingId: string
  key: string
  abcText: string
  submitterName: string
  voteScore: number
  userVoteValue: number // -1, 0, or 1
}

export interface TuneDetails {
  tuneId: string
  title: string
  tuneType: string
  key: string
  aliases: string[]
  settings: TuneSetting[]
}

export interface RepertoireItem {
  userTuneId: string
  tuneId: string
  title: string
  tuneType: string
  key: string
  status: string
  lastPracticed: string | null
  targetBpm: number | null
  abc?: string
  abcText?: string
  hasCustomAbc: boolean
  /** Rough 0–100 difficulty estimate (higher = harder). */
  difficulty?: number
  /** First/last note of the master ABC as absolute MIDI; used for set-medley joins. */
  startingMidi?: number | null
  endingMidi?: number | null
  /** The setting this entry shows — the user's pin, or the tune's top-voted setting when unpinned. */
  tuneSettingId?: string | null
  settingSubmitterName?: string | null
  /** True when the user explicitly chose the setting; false when it was resolved from votes. */
  isPinnedSetting?: boolean
}

/** Lightweight repertoire row for the scrolling list — no ABC. Fetch getRepertoireItem for that. */
export interface RepertoireListItem {
  userTuneId: string
  tuneId: string
  title: string
  tuneType: string
  key: string
  status: string
  lastPracticed: string | null
  targetBpm: number | null
  /** Rough 0–100 difficulty estimate (higher = harder). */
  difficulty?: number
}

export interface PracticeQueueItem {
  userTuneId: string
  title: string
  tuneType: string
  key: string
  status: string
  lastPracticed: string | null
  targetBpm: number | null
  abc: string
  abcText?: string
  reason: string
}

export interface KeyTransition {
  fromTitle: string
  toTitle: string
  fromKey: string
  toKey: string
  fifthsApart: number
  label: string
}

export interface SetAnalysis {
  totalSeconds: number
  totalPlaytime: string
  transitions: KeyTransition[]
}

export interface SetItem {
  userTuneId: string
  tuneId: string
  sequenceNumber: number
  repetitions: number
  title: string
  tuneType: string
  key: string
  abc: string
}

export interface SaveSetItem {
  userTuneId: string
  repetitions: number
}

export interface TuneSet {
  setId: string
  title: string
  items: SetItem[]
  analysis: SetAnalysis
}

// Lightweight set row for the list view — no ABC payload. Fetch the singular set (getSet) for that.
export interface SetItemSummary {
  userTuneId: string
  sequenceNumber: number
  repetitions: number
  title: string
  tuneType: string
  key: string
}

export interface TuneSetSummary {
  setId: string
  title: string
  items: SetItemSummary[]
  analysis: SetAnalysis
}

export const TUNE_STATUSES = ['Wishlist', 'Learning', 'Active', 'Rusty', 'Retired'] as const

export const TUNE_TYPES = [
  'Reel', 'Jig', 'SlipJig', 'Hornpipe', 'Polka', 'Slide', 'Waltz',
  'March', 'Strathspey', 'Barndance', 'Mazurka', 'Air', 'Other',
] as const

export function getDefaultTargetBpm(tuneType: string): number {
  switch (tuneType) {
    case 'Reel':
    case 'Jig':
    case 'SlipJig':
    case 'Waltz':
    case 'Mazurka':
    case 'Other':
    default:
      return 110
    case 'Hornpipe':
    case 'March':
      return 90
    case 'Polka':
      return 115
    case 'Slide':
      return 120
    case 'Strathspey':
      return 80
    case 'Barndance':
      return 95
    case 'Air':
      return 60
  }
}

// --- Endpoints ---

export interface PagedResult<T> {
  items: T[]
  totalCount: number
  page: number
  pageSize: number
  hasMore: boolean
}

export const searchTunes = (search: string, type?: string, sort?: string, page = 1, pageSize = 20) => {
  const params = new URLSearchParams()
  if (search) params.set('search', search)
  if (type) params.set('type', type)
  if (sort) params.set('sort', sort)
  params.set('page', page.toString())
  params.set('pageSize', pageSize.toString())
  return apiFetchPublic<PagedResult<TuneSummary>>(`/tunes?${params}`)
}


export const createTune = (tune: { title: string; tuneType: string; key: string; abc: string }) =>
  apiFetch<TuneSummary>('/tunes', { method: 'POST', body: JSON.stringify(tune) })

/** thesession.org's own numeric tune ID — not one of our UUIDs. */
export interface TheSessionResult {
  theSessionId: number
  name: string
  type: string
}

export const searchTheSession = (q: string) =>
  apiFetchPublic<TheSessionResult[]>(`/import/thesession/search?q=${encodeURIComponent(q)}`)

export const importFromTheSession = (theSessionId: number) =>
  apiFetch<TuneSummary>('/import/thesession', {
    method: 'POST',
    body: JSON.stringify({ theSessionId }),
  })

export interface TunebookImportResult {
  totalFound: number
  imported: number
  addedToRepertoire: number
  alreadyInRepertoire: number
  failures: string[]
}

export const importTunebook = (memberId: string) =>
  apiFetch<TunebookImportResult>('/import/thesession/tunebook', {
    method: 'POST',
    body: JSON.stringify({ memberId: Number(memberId) }),
  })

export const getRepertoire = (search?: string, type?: string, status?: string, page = 1, pageSize = 20) => {
  const params = new URLSearchParams()
  if (search) params.set('search', search)
  if (type) params.set('type', type)
  if (status) params.set('status', status)
  params.set('page', page.toString())
  params.set('pageSize', pageSize.toString())
  return apiFetch<PagedResult<RepertoireItem>>(`/repertoire?${params}`)
}

/** Lightweight rows (no ABC) for the scrolling Repertoire list — call getRepertoireItem for detail. */
export const getRepertoireList = (search?: string, type?: string, status?: string, page = 1, pageSize = 20) => {
  const params = new URLSearchParams()
  if (search) params.set('search', search)
  if (type) params.set('type', type)
  if (status) params.set('status', status)
  params.set('page', page.toString())
  params.set('pageSize', pageSize.toString())
  params.set('summary', 'true')
  return apiFetch<PagedResult<RepertoireListItem>>(`/repertoire?${params}`)
}

export const getRepertoireItem = (userTuneId: string) =>
  apiFetch<RepertoireItem>(`/repertoire/items/${userTuneId}`)

/** Full repertoire (with ABC), all pages — for bulk operations like CSV/JSON backup export. */
export const getFullRepertoire = async (): Promise<RepertoireItem[]> => {
  const items: RepertoireItem[] = []
  let page = 1
  for (;;) {
    const result = await getRepertoire(undefined, undefined, undefined, page, 100)
    items.push(...result.items)
    if (!result.hasMore) break
    page += 1
  }
  return items
}

/** Lightweight full repertoire (no ABC), all pages — for ID/membership-only checks. */
export const getFullRepertoireList = async (): Promise<RepertoireListItem[]> => {
  const items: RepertoireListItem[] = []
  let page = 1
  for (;;) {
    const result = await getRepertoireList(undefined, undefined, undefined, page, 100)
    items.push(...result.items)
    if (!result.hasMore) break
    page += 1
  }
  return items
}

export interface RepertoireStats {
  totalCount: number
  byStatus: Record<string, number>
  byType: Record<string, number>
  rustyCount: number
}

/** Aggregate repertoire counts for the dashboard — avoids fetching every row. */
export const getRepertoireStats = () => apiFetch<RepertoireStats>('/repertoire/stats')

export const addToRepertoire = (tuneId: string, tuneSettingId?: string) =>
  apiFetch<RepertoireItem>('/repertoire', {
    method: 'POST',
    body: JSON.stringify({ tuneId, tuneSettingId }),
  })

export const getTuneDetails = (tuneId: string, _userId?: string) =>
  apiFetchPublic<TuneDetails>(`/tunes/${tuneId}`)

export const addTuneSetting = (tuneId: string, key: string, abc: string, submitterName: string) =>
  apiFetch<TuneSetting>(`/tunes/${tuneId}/settings`, {
    method: 'POST',
    body: JSON.stringify({ key, abc, submitterName }),
  })

export const addTuneAlias = (tuneId: string, name: string) =>
  apiFetch<{ name: string }>(`/tunes/${tuneId}/aliases`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  })

export interface TuneMatchCandidate {
  tuneId: string
  title: string
  tuneType: string
  key: string
  difficulty: number
  /** 0-100. See api MelodyFingerprint.ConfidenceThresholdPercent for the "confident" cutoff. */
  confidence: number
  matchedTuneSettingId: string | null
}

export interface TuneMatchResult {
  candidates: TuneMatchCandidate[]
  hasConfidentMatch: boolean
}

/** Identifies a tune from an ordered sequence of MIDI notes captured live (see tuneMatcher.ts). */
export const matchTune = (notes: number[]) =>
  apiFetchPublic<TuneMatchResult>('/tunes/match', { method: 'POST', body: JSON.stringify({ notes }) })

export const voteTuneSetting = (settingId: string, value: number) =>
  apiFetch<{ voteScore: number }>(`/tunes/settings/${settingId}/vote`, {
    method: 'POST',
    body: JSON.stringify({ value }),
  })

export const getMyTuneVotes = (tuneId: string) =>
  apiFetch<Record<string, number>>(`/tunes/${tuneId}/my-votes`)

export const updateUserTune = (
  userTuneId: string,
  patch: {
    status?: string
    targetBpm?: number
    customAbcText?: string
    tuneSettingId?: string
    /** Un-pins the setting so the entry follows the tune's top-voted one. Mutually exclusive with tuneSettingId. */
    clearTuneSetting?: boolean
  },
) =>
  apiFetch<RepertoireItem>(`/repertoire/items/${userTuneId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })

/** Drops the pinned setting so the entry follows the tune's community default again. */
export const clearUserTuneSetting = (userTuneId: string) =>
  updateUserTune(userTuneId, { clearTuneSetting: true })

export const logPractice = (userTuneId: string) =>
  apiFetch<{ userTuneId: string; lastPracticed: string; newBadges: string[] }>(
    `/repertoire/items/${userTuneId}/practiced`,
    { method: 'POST' },
  )

export interface IntervalRoundResult {
  /** 0 once the day's scored rounds are used up. */
  pointsAwarded: number
  dailyCapReached: boolean
  newBadges: string[]
}

/** Records a finished interval round for points and badges. roundId makes a retry a no-op. */
export const recordIntervalRound = (body: {
  roundId: string
  questionCount: number
  correctCount: number
  answerMode: 'play' | 'name'
}) =>
  apiFetch<IntervalRoundResult>('/users/me/interval-rounds', {
    method: 'POST',
    body: JSON.stringify(body),
  })

export interface EarTrainingProgress {
  userTuneId: string
  phraseIndex: number
  notesRevealed: number
  completedAt: string | null
}

export const getEarTrainingProgress = (userTuneId: string) =>
  apiFetch<EarTrainingProgress>(`/repertoire/items/${userTuneId}/ear-training`)

export const saveEarTrainingProgress = (
  userTuneId: string,
  body: { phraseIndex: number; notesRevealed: number; completed: boolean },
) =>
  apiFetch<EarTrainingProgress>(`/repertoire/items/${userTuneId}/ear-training`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })

export const removeFromRepertoire = (userTuneId: string) =>
  apiFetch<void>(`/repertoire/items/${userTuneId}`, { method: 'DELETE' })

export interface PracticeDay {
  date: string
  count: number
}

export interface PracticeStats {
  currentStreakDays: number
  totalLast30: number
  perDay: PracticeDay[]
}

export const getPracticeStats = () => apiFetch<PracticeStats>('/practice/stats')

export interface UserBadge {
  badgeKey: string
  earnedAt: string
}

export interface GamificationSummary {
  totalPoints: number
  badges: UserBadge[]
}

export const getMyGamificationSummary = () => apiFetch<GamificationSummary>('/users/me/gamification')

export const getPublicGamificationSummary = (userId: string) =>
  apiFetchPublic<GamificationSummary>(`/users/${userId}/gamification`)

export const getPracticeQueue = () =>
  apiFetch<PracticeQueueItem[]>('/practice/queue')

export interface NextTuneSuggestion {
  userTuneId: string
  title: string
  tuneType: string
  key: string
  status: string
  score: number
  reason: string
}

export const suggestNextTune = (userTuneIds: string[]) =>
  apiFetch<NextTuneSuggestion[]>('/sets/suggest', {
    method: 'POST',
    body: JSON.stringify({ userTuneIds }),
  })

export const getSets = () => apiFetch<TuneSetSummary[]>('/sets')

export const getSet = (setId: string) => apiFetchPublic<TuneSet>(`/sets/${setId}`)

export const createSet = (title: string, items: SaveSetItem[]) =>
  apiFetch<TuneSet>('/sets', {
    method: 'POST',
    body: JSON.stringify({ title, items }),
  })

export const updateSet = (setId: string, title: string, items: SaveSetItem[]) =>
  apiFetch<TuneSet>(`/sets/${setId}`, {
    method: 'PUT',
    body: JSON.stringify({ title, items }),
  })

export const deleteSet = (setId: string) =>
  apiFetch<void>(`/sets/${setId}`, { method: 'DELETE' })

// --- Groups Types & Functions ---
export type RecurrencePattern = 'None' | 'Weekly' | 'Biweekly' | 'MonthlyNthDay' | 'SpecificDate'

export interface GroupScheduleExceptionData {
  groupScheduleExceptionId: string
  groupMeetingScheduleId: string
  exceptionDate: string
}

export interface GroupMeetingScheduleData {
  groupMeetingScheduleId: string
  groupId: string
  name?: string | null
  recurrencePattern: RecurrencePattern
  recurrenceDayOfWeek: number | null
  recurrenceWeekOfMonth: number | null
  startDate: string | null
  meetingTime: string | null
  meetingScheduleText: string | null
  meetingDescription: string | null
  exceptions: GroupScheduleExceptionData[]
}

export interface GroupUpdateData {
  groupUpdateId: string
  groupId: string
  title: string
  content: string
  createdByUserId: string
  createdByDisplayName: string
  createdAt: string
}

export interface GroupSummary {
  groupId: string
  name: string
  description: string | null
  city: string | null
  memberCount: number
  isMember: boolean
  isPrivate: boolean
  isPending: boolean
  timeZone?: string | null
}

export type GroupRole = 'Member' | 'Administrator' | 'Owner'

export interface GroupMember {
  userId: string
  displayName: string
  role: GroupRole
}

export interface GroupCommonTune {
  tuneId: string
  title: string
  tuneType: string
  key: string
  memberCount: number
  totalMembersInGroup: number
}

export type GroupFeedEventType = 'TuneLearned' | 'MemberJoined' | 'GroupTuneAdded' | 'GroupTuneRemoved'
  | 'SessionScheduled' | 'SessionCancelled' | 'AnnouncementPosted' | 'GroupSetCreated' | 'StreakMilestone' | 'BadgeEarned'

export interface GroupFeedEventData {
  memberName: string
  eventType: GroupFeedEventType
  tuneTitles: string[]
  createdAt: string
  /** Pre-built action clause for event types that aren't rendered from tuneTitles (sessions, announcements, sets, milestones). */
  detail?: string
}

export interface GroupTuneListItem {
  groupTuneId: string
  tuneId: string
  title: string
  tuneType: string
  key: string
  tuneSettingId: string | null
  settingSubmitterName: string | null
  addedByDisplayName: string
}

export interface GroupDetails {
  groupId: string
  name: string
  description: string | null
  city: string | null
  isPrivate: boolean
  inviteCode: string | null
  viewerStatus: 'Active' | 'Pending' | null
  members: GroupMember[]
  pendingMembers: GroupMember[]
  masterTunes: GroupTuneListItem[]
  commonTunes: GroupCommonTune[]
  feed: GroupFeedEventData[]
  updates: GroupUpdateData[]
  schedules: GroupMeetingScheduleData[]
  timeZone?: string | null
}

export interface JoinResult {
  status: 'Active' | 'Pending'
}

export const getGroups = (userId: string, search?: string, page = 1, pageSize = 20) => {
  const params = new URLSearchParams()
  if (search) params.set('search', search)
  params.set('page', page.toString())
  params.set('pageSize', pageSize.toString())
  return userId
    ? apiFetch<PagedResult<GroupSummary>>(`/groups?${params}`)
    : apiFetchPublic<PagedResult<GroupSummary>>(`/groups?${params}`)
}

export const getGroupDetails = (groupId: string, userId: string) =>
  userId ? apiFetch<GroupDetails>(`/groups/${groupId}`) : apiFetchPublic<GroupDetails>(`/groups/${groupId}`)

export interface CreateGroupParams {
  name: string
  description: string
  city?: string
  isPrivate?: boolean
  timeZone?: string
  recurrencePattern?: RecurrencePattern
  recurrenceDayOfWeek?: number | null
  recurrenceWeekOfMonth?: number | null
  startDate?: string | null
  meetingTime?: string
  meetingScheduleText?: string
  meetingDescription?: string
  schedules?: Array<{
    name?: string
    recurrencePattern: RecurrencePattern
    recurrenceDayOfWeek?: number | null
    recurrenceWeekOfMonth?: number | null
    startDate?: string | null
    meetingTime?: string
    meetingScheduleText?: string
    meetingDescription?: string
  }>
}

export const createGroup = (params: CreateGroupParams) =>
  apiFetch<GroupSummary>('/groups', {
    method: 'POST',
    body: JSON.stringify(params),
  })

export const updateGroupInfo = (
  groupId: string,
  info: { name: string; description: string; city?: string; isPrivate: boolean; timeZone?: string },
) =>
  apiFetch<void>(`/groups/${groupId}/info`, {
    method: 'POST',
    body: JSON.stringify(info),
  })

export const replaceGroupSchedules = (
  groupId: string,
  schedules: Array<{
    name?: string
    recurrencePattern: RecurrencePattern
    recurrenceDayOfWeek?: number | null
    recurrenceWeekOfMonth?: number | null
    startDate?: string | null
    meetingTime?: string
    meetingScheduleText?: string
    meetingDescription?: string
  }>,
) =>
  apiFetch<void>(`/groups/${groupId}/schedules/replace`, {
    method: 'POST',
    body: JSON.stringify({ schedules }),
  })

export const createGroupUpdate = (groupId: string, title: string, content: string) =>
  apiFetch<GroupUpdateData>(`/groups/${groupId}/updates`, {
    method: 'POST',
    body: JSON.stringify({ title, content }),
  })

export const deleteGroupUpdate = (groupId: string, updateId: string) =>
  apiFetch<void>(`/groups/${groupId}/updates/${updateId}`, {
    method: 'DELETE',
  })

export const updateGroupSchedule = (
  groupId: string,
  scheduleConfig: {
    name?: string
    recurrencePattern: RecurrencePattern
    recurrenceDayOfWeek?: number | null
    recurrenceWeekOfMonth?: number | null
    startDate?: string | null
    meetingTime?: string
    meetingScheduleText?: string
    meetingDescription?: string
  },
) =>
  apiFetch<void>(`/groups/${groupId}/schedule`, {
    method: 'POST',
    body: JSON.stringify(scheduleConfig),
  })

export const addGroupSchedule = (
  groupId: string,
  scheduleConfig: {
    name?: string
    recurrencePattern: RecurrencePattern
    recurrenceDayOfWeek?: number | null
    recurrenceWeekOfMonth?: number | null
    startDate?: string | null
    meetingTime?: string
    meetingScheduleText?: string
    meetingDescription?: string
  },
) =>
  apiFetch<GroupMeetingScheduleData>(`/groups/${groupId}/schedules`, {
    method: 'POST',
    body: JSON.stringify(scheduleConfig),
  })

export const deleteGroupSchedule = (groupId: string, scheduleId: string) =>
  apiFetch<void>(`/groups/${groupId}/schedules/${scheduleId}`, {
    method: 'DELETE',
  })

export const addGroupScheduleException = (groupId: string, scheduleId: string, exceptionDate: string) =>
  apiFetch<GroupScheduleExceptionData>(`/groups/${groupId}/schedules/${scheduleId}/exceptions`, {
    method: 'POST',
    body: JSON.stringify({ exceptionDate }),
  })

export const deleteGroupScheduleException = (groupId: string, scheduleId: string, exceptionId: string) =>
  apiFetch<void>(`/groups/${groupId}/schedules/${scheduleId}/exceptions/${exceptionId}`, {
    method: 'DELETE',
  })

export const joinGroup = (groupId: string, inviteCode?: string) =>
  apiFetch<JoinResult>(`/groups/${groupId}/join`, {
    method: 'POST',
    body: JSON.stringify({ inviteCode }),
  })

export const updateGroupPrivacy = (groupId: string, isPrivate: boolean) =>
  apiFetch<void>(`/groups/${groupId}/privacy`, {
    method: 'PATCH',
    body: JSON.stringify({ isPrivate }),
  })

export const regenerateGroupInviteCode = (groupId: string) =>
  apiFetch<{ inviteCode: string }>(`/groups/${groupId}/invite-code`, {
    method: 'POST',
  })

export const approveGroupMember = (groupId: string, targetUserId: string) =>
  apiFetch<void>(`/groups/${groupId}/members/${targetUserId}/approve`, {
    method: 'POST',
  })

export const rejectGroupMember = (groupId: string, targetUserId: string) =>
  apiFetch<void>(`/groups/${groupId}/members/${targetUserId}/reject`, {
    method: 'POST',
  })

export const leaveGroup = (groupId: string) =>
  apiFetch<void>(`/groups/${groupId}/leave`, {
    method: 'POST',
  })

export const removeGroupMember = (groupId: string, targetUserId: string) =>
  apiFetch<void>(`/groups/${groupId}/members/${targetUserId}`, {
    method: 'DELETE',
  })

export const deleteGroup = (groupId: string) =>
  apiFetch<void>(`/groups/${groupId}`, {
    method: 'DELETE',
  })


export const grantGroupAdministrator = (groupId: string, targetUserId: string) =>
  apiFetch<void>(`/groups/${groupId}/members/${targetUserId}/promote`, {
    method: 'POST',
  })

export const revokeGroupAdministrator = (groupId: string, targetUserId: string) =>
  apiFetch<void>(`/groups/${groupId}/members/${targetUserId}/demote`, {
    method: 'POST',
  })

export const transferGroupOwnership = (groupId: string, newOwnerUserId: string) =>
  apiFetch<void>(`/groups/${groupId}/transfer-ownership`, {
    method: 'POST',
    body: JSON.stringify({ newOwnerUserId }),
  })

export const addGroupTune = (groupId: string, tuneId: string, tuneSettingId?: string) =>
  apiFetch<GroupTuneListItem>(`/groups/${groupId}/tunes`, {
    method: 'POST',
    body: JSON.stringify({ tuneId, tuneSettingId }),
  })

export const updateGroupTune = (groupId: string, groupTuneId: string, tuneSettingId?: string) =>
  apiFetch<GroupTuneListItem>(`/groups/${groupId}/tunes/${groupTuneId}`, {
    method: 'PATCH',
    body: JSON.stringify({ tuneSettingId }),
  })

export const removeGroupTune = (groupId: string, groupTuneId: string) =>
  apiFetch<void>(`/groups/${groupId}/tunes/${groupTuneId}`, {
    method: 'DELETE',
  })

// --- Group Set Lists ---

export interface GroupSetItemData {
  groupTuneId: string
  sequenceNumber: number
  repetitions: number
  title: string
  tuneType: string
  key: string
  abc: string
}

export interface GroupSetData {
  groupSetId: string
  groupId: string
  title: string
  items: GroupSetItemData[]
  analysis: SetAnalysis
}

// Lightweight group-set row for the list view — no ABC payload. Fetch the singular set (getGroupSet) for that.
export interface GroupSetItemSummaryData {
  groupTuneId: string
  sequenceNumber: number
  repetitions: number
  title: string
  tuneType: string
  key: string
}

export interface GroupSetSummaryData {
  groupSetId: string
  groupId: string
  title: string
  items: GroupSetItemSummaryData[]
  analysis: SetAnalysis
}

export interface SaveGroupSetItem {
  groupTuneId: string
  repetitions: number
}

export const getGroupSets = (groupId: string, userId: string) =>
  userId
    ? apiFetch<GroupSetSummaryData[]>(`/groups/${groupId}/sets`)
    : apiFetchPublic<GroupSetSummaryData[]>(`/groups/${groupId}/sets`)

export const getGroupSet = (groupSetId: string, userId: string) =>
  userId ? apiFetch<GroupSetData>(`/groupsets/${groupSetId}`) : apiFetchPublic<GroupSetData>(`/groupsets/${groupSetId}`)

export const createGroupSet = (groupId: string, title: string, items: SaveGroupSetItem[]) =>
  apiFetch<GroupSetData>(`/groups/${groupId}/sets`, {
    method: 'POST',
    body: JSON.stringify({ title, items }),
  })

export const updateGroupSet = (groupSetId: string, title: string, items: SaveGroupSetItem[]) =>
  apiFetch<GroupSetData>(`/groupsets/${groupSetId}`, {
    method: 'PUT',
    body: JSON.stringify({ title, items }),
  })

export const deleteGroupSet = (groupSetId: string) =>
  apiFetch<void>(`/groupsets/${groupSetId}`, { method: 'DELETE' })

// --- Scheduled Sessions ---

export type RsvpStatus = 'Going' | 'Maybe' | 'NotGoing'

export interface RsvpAttendee {
  userId: string
  displayName: string
  status: RsvpStatus
}

export interface GroupSessionData {
  groupSessionId: string
  title: string
  location: string | null
  startsAt: string
  notes: string | null
  isCancelled: boolean
  createdByDisplayName: string
  goingCount: number
  maybeCount: number
  notGoingCount: number
  viewerRsvpStatus: RsvpStatus | null
  attendees?: RsvpAttendee[]
}

export const getGroupSessions = (groupId: string, userId: string) =>
  userId
    ? apiFetch<GroupSessionData[]>(`/groups/${groupId}/sessions`)
    : apiFetchPublic<GroupSessionData[]>(`/groups/${groupId}/sessions`)

export const createGroupSession = (
  groupId: string,
  fields: { title: string; location?: string; startsAt: string; notes?: string; repeatWeeks: number },
) =>
  apiFetch<GroupSessionData[]>(`/groups/${groupId}/sessions`, {
    method: 'POST',
    body: JSON.stringify(fields),
  })

export const updateGroupSession = (
  groupId: string,
  sessionId: string,
  patch: { title?: string; location?: string; startsAt?: string; notes?: string; isCancelled?: boolean },
) =>
  apiFetch<GroupSessionData>(`/groups/${groupId}/sessions/${sessionId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })

export const deleteGroupSession = (groupId: string, sessionId: string) =>
  apiFetch<void>(`/groups/${groupId}/sessions/${sessionId}`, { method: 'DELETE' })

export const rsvpToGroupSession = (groupId: string, sessionId: string, status: RsvpStatus) =>
  apiFetch<GroupSessionData>(`/groups/${groupId}/sessions/${sessionId}/rsvp`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  })

export const rsvpToGroupScheduleDate = (groupId: string, scheduleId: string, date: string, status: RsvpStatus) =>
  apiFetch<GroupSessionData>(`/groups/${groupId}/schedules/${scheduleId}/rsvp`, {
    method: 'POST',
    body: JSON.stringify({ date, status }),
  })

export interface UpcomingMeetingData {
  groupId: string
  groupName: string
  groupSessionId: string | null
  groupMeetingScheduleId: string | null
  title: string
  location: string | null
  startsAt: string
  isCancelled: boolean
  viewerRsvpStatus: RsvpStatus | null
  goingCount: number
  maybeCount: number
}

export const getUpcomingMeetings = () => apiFetch<UpcomingMeetingData[]>('/me/upcoming-meetings')

export interface DashboardActivityFeedItem {
  groupFeedEventId: string
  groupId: string
  groupName: string
  eventType: string
  summaryText: string
  createdAt: string
}

export interface DashboardRustyTune {
  userTuneId: string
  title: string
  tuneType: string
  lastPracticed: string | null
}

export interface DashboardPendingRequest {
  groupId: string
  groupName: string
  pendingCount: number
}

export interface DashboardFriendActivity {
  activityId: string
  friendUserId: string
  friendDisplayName: string
  friendUsername: string | null
  activityType: 'SessionRsvp' | 'TuneLearned' | 'BadgeEarned' | 'NewFriend'
  summaryText: string
  createdAt: string
  targetUrl?: string | null
}

export interface DashboardSummaryData {
  currentStreakDays: number
  recentActivity: DashboardActivityFeedItem[]
  topRustyTunes: DashboardRustyTune[]
  pendingGroupRequests: DashboardPendingRequest[]
  recentFriendActivity?: DashboardFriendActivity[]
}

export const getDashboardSummary = () => apiFetch<DashboardSummaryData>('/me/dashboard-summary')

export interface UserProfile {
  userId: string
  displayName: string
  username: string | null
  email: string
  bio: string | null
  location: string | null
  instruments: string | null
  isModerator: boolean
  isProfilePrivate: boolean
}

export const getCurrentUser = () => apiFetch<UserProfile>('/users/me')

export const updateCurrentUser = (patch: { displayName?: string; bio?: string; location?: string; instruments?: string; username?: string; isProfilePrivate?: boolean }) =>
  apiFetch<UserProfile>('/users/me', { method: 'PATCH', body: JSON.stringify(patch) }, { skipOfflineQueue: true })

export interface UsernameAvailability {
  username: string
  available: boolean
  reason: string | null
}

export const checkUsernameAvailable = (username: string) =>
  apiFetchPublic<UsernameAvailability>(`/users/username-available/${encodeURIComponent(username)}`)

export interface PublicUserProfile {
  userId: string
  displayName: string
  username: string
  bio: string | null
  location: string | null
  instruments: string | null
  /** Counts carried here so the profile tabs can be labelled without fetching either list. */
  activeTuneCount: number
  setCount: number
}

export const getPublicUserProfile = (username: string) =>
  apiFetchPublic<PublicUserProfile>(`/users/by-username/${encodeURIComponent(username)}/profile`)

export interface PublicActiveTune {
  tuneId: string
  title: string
  tuneType: string
  key: string
}

export const getPublicActiveTunes = (userId: string) =>
  apiFetchPublic<PublicActiveTune[]>(`/users/${userId}/active-tunes`)

/** A user's sets, as shown on their public profile. Sets follow profile privacy, so this is keyed
 *  on the username slug and 404s for anyone without a public profile. */
export const getPublicUserSets = (username: string) =>
  apiFetchPublic<TuneSetSummary[]>(`/users/by-username/${encodeURIComponent(username)}/sets`)

// Moderator Interfaces and API Methods
export interface ModeratorTuneData {
  tuneId: string
  title: string
  tuneType: string
  key: string
  masterAbcText: string
  isReviewed: boolean
  reviewedAt: string | null
  reviewedBy: string | null
  createdAt: string
}

export interface ModeratorGroupData {
  groupId: string
  name: string
  description: string
  city: string | null
  isPrivate: boolean
  inviteCode: string | null
  isReviewed: boolean
  reviewedAt: string | null
  reviewedBy: string | null
  createdAt: string
  memberCount: number
}

export interface ModeratorUserDetails {
  userId: string
  displayName: string
  username: string | null
  email: string
  bio: string | null
  location: string | null
  instruments: string | null
  isModerator: boolean
  groupCount: number
  tuneCount: number
}

export const getUnreviewedTunes = () =>
  apiFetch<ModeratorTuneData[]>('/moderator/unreviewed-tunes')

export const getUnreviewedGroups = () =>
  apiFetch<ModeratorGroupData[]>('/moderator/unreviewed-groups')

export const approveTune = (tuneId: string) =>
  apiFetch<ModeratorTuneData>(`/moderator/tunes/${tuneId}/approve`, { method: 'POST' })

export const approveGroup = (groupId: string) =>
  apiFetch<ModeratorGroupData>(`/moderator/groups/${groupId}/approve`, { method: 'POST' })

export const moderatorUpdateTune = (tuneId: string, payload: { title: string; tuneType: string; key: string; masterAbcText: string }) =>
  apiFetch<ModeratorTuneData>(`/moderator/tunes/${tuneId}`, { method: 'PUT', body: JSON.stringify(payload) })

export const moderatorDeleteTune = (tuneId: string) =>
  apiFetch<void>(`/moderator/tunes/${tuneId}`, { method: 'DELETE' })

export const moderatorDeleteTuneSetting = (settingId: string) =>
  apiFetch<void>(`/moderator/tunes/settings/${settingId}`, { method: 'DELETE' })

export const moderatorDeleteTuneAlias = (aliasId: string) =>
  apiFetch<void>(`/moderator/tunes/aliases/${aliasId}`, { method: 'DELETE' })

export const getModeratorGroups = () =>
  apiFetch<ModeratorGroupData[]>('/moderator/groups')

export const getModeratorUserDetails = (userId: string) =>
  apiFetch<ModeratorUserDetails>(`/moderator/users/${userId}`)

export const sanitizeUserProfile = (userId: string, payload: { displayName?: string; bio?: string; location?: string; instruments?: string }) =>
  apiFetch<{ success: boolean; userId: string; displayName: string }>(`/moderator/users/${userId}/sanitize`, { method: 'POST', body: JSON.stringify(payload) })

export interface RecomputeHeuristicsResult {
  tunesUpdated: number
  settingsUpdated: number
}

/**
 * Backfills the stored melodic heuristics and match fingerprint for every tune and setting.
 * Tunes predating the identification feature have no fingerprint, so they can never be
 * identified until this has run. Long-running (it walks the whole library in batches).
 */
export const moderatorRecomputeHeuristics = () =>
  apiFetch<RecomputeHeuristicsResult>(
    '/moderator/tunes/recompute-heuristics',
    { method: 'POST' },
    // Walks and rewrites the entire library, so the default 20s request timeout would abort a
    // job the server is still happily running. Not queueable either — see handleUnreachable.
    { skipOfflineQueue: true, timeoutMs: 10 * 60 * 1000 },
  )

/**
 * Forces the in-memory tune-identification index to rebuild now rather than on its 30-minute
 * TTL. Run after a backfill. Note this only refreshes the Public Functions instance that
 * happens to serve the request — on a scaled-out deployment, others follow on their own TTL.
 */
export const moderatorRefreshMatchIndex = () =>
  apiFetchPublicAuthed<void>('/moderator/tunes/refresh-match-index', { method: 'POST' })

export interface FriendDto {
  userId: string
  displayName: string
  username: string | null
  bio: string | null
  location: string | null
  instruments: string | null
  friendsSince: string
}

export interface FriendRequestDto {
  friendshipId: string
  requesterId: string
  requesterDisplayName: string
  requesterUsername: string | null
  requestedAt: string
}

export interface FriendshipStatusDto {
  status: 'Self' | 'None' | 'PendingSent' | 'PendingReceived' | 'Accepted'
  friendshipId: string | null
}

export const getFriendshipStatus = (targetUserId: string) =>
  apiFetch<FriendshipStatusDto>(`/friends/status/${targetUserId}`)

export const sendFriendRequest = (targetUserId: string) =>
  apiFetch<FriendshipStatusDto>(`/friends/request/${targetUserId}`, { method: 'POST' })

export const acceptFriendRequest = (friendshipId: string) =>
  apiFetch<FriendshipStatusDto>(`/friends/accept/${friendshipId}`, { method: 'POST' })

export const declineFriendRequest = (friendshipId: string) =>
  apiFetch<FriendshipStatusDto>(`/friends/decline/${friendshipId}`, { method: 'POST' })

export const cancelFriendRequest = (friendshipId: string) =>
  apiFetch<FriendshipStatusDto>(`/friends/cancel/${friendshipId}`, { method: 'POST' })

export const removeFriend = (targetUserId: string) =>
  apiFetch<FriendshipStatusDto>(`/friends/${targetUserId}`, { method: 'DELETE' })

export const getFriendsList = () =>
  apiFetch<FriendDto[]>('/friends')

export const getPendingFriendRequests = () =>
  apiFetch<FriendRequestDto[]>('/friends/requests')

export interface DashboardActivityItemData {
  activityId: string
  source: 'Group' | 'Friend'
  activityType: string
  summaryText: string
  createdAt: string
  authorDisplayName?: string | null
  authorUsername?: string | null
  groupId?: string | null
  groupName?: string | null
  targetUrl?: string | null
}

export const getActivityFeed = (filter = 'all', page = 1, pageSize = 20) =>
  apiFetch<PagedResult<DashboardActivityItemData>>(`/me/activity-feed?filter=${encodeURIComponent(filter)}&page=${page}&pageSize=${pageSize}`)
